import {Clock, Coffee, Keyboard, MapPin, Pencil, Plus, Sparkles, Trash2, X} from 'lucide-react'
import React, {FC, useId, useMemo, useState} from 'react'
import {createPortal} from 'react-dom'
import {IntlShape, useIntl} from 'react-intl'

import {WizardData} from '../index'
import {branchIdOf, isFirstBranch, removeBranches, renameBranch} from '../utils/pendingDeletes'
import {
  DAY_LETTERS,
  DEFAULT_BRANCH_NAME,
  DEFAULT_LECTURE_DURATION,
  DEFAULT_TIMING_DAYS,
  DEFAULT_TIMING_END,
  DEFAULT_TIMING_START,
  WEEKDAYS,
  formatTime24to12,
  getMidpointBreak,
  normalizeDay,
  timeToMinutes,
} from './Step2BasicSetup'
import BranchRemovalDialog, {BranchRemovalRequest} from './BranchRemovalDialog'
import WizardDatePicker from './WizardDatePicker'
import WizardTimePicker from './WizardTimePicker'

/** The Institution Setup sections the review page's edit sheet can host. */
export type EditableSection =
  | 'branches'
  | 'duration'
  | 'frequency'
  | 'venues'
  | 'timings'
  | 'breaks'

type ManualData = WizardData['manualData']
type TimingRow = {day: string; startTime: string; endTime: string; lectureDuration?: number}
type BreakRow = {day: string; startTime: string; endTime: string}
/** One start/end pair in the break form; a day may hold several. */
type BreakSlot = {startTime: string; endTime: string}

/** Structural equality for the editors' dirty checks (draft vs. what the sheet opened with). */
const sameJson = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b)

const minutesToTime = (mins: number) =>
  `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`

/** Stable order for break rows: weekday first, then start time. */
const sortBreaks = (list: BreakRow[]): BreakRow[] =>
  [...list].sort((a, b) => {
    const dayDiff =
      WEEKDAYS.indexOf(normalizeDay(a.day) as (typeof WEEKDAYS)[number]) -
      WEEKDAYS.indexOf(normalizeDay(b.day) as (typeof WEEKDAYS)[number])
    return dayDiff !== 0 ? dayDiff : timeToMinutes(a.startTime) - timeToMinutes(b.startTime)
  })

// Same bounds the legacy Step2BasicSetup section enforces on the native date inputs.
const SESSION_DATE_MIN = '0001-01-01'
const SESSION_DATE_MAX = '9999-12-31'

interface Props {
  section: EditableSection
  data: WizardData
  updateData: (data: Partial<WizardData>, options?: {prefill?: boolean}) => void
  isDark: boolean
  /** Close the hosting sheet (after a commit, or on Cancel). */
  onDone: () => void
  /** Branch saves also run the incremental branches sync, like the old sub-step advance did. */
  onPartialSubmit?: (fields: any[], manualData?: ManualData, cb?: () => void) => void
  isSubmitting?: boolean
}

/** Shared color tokens so every section editor reads as one surface. */
const makeTokens = (isDark: boolean) => ({
  isDark,
  text: isDark ? '#ffffff' : '#181C32',
  muted: isDark ? '#92929f' : '#7E8299',
  border: isDark ? '#3f4254' : '#e4e6ef',
  panelBg: isDark ? 'rgba(255,255,255,0.04)' : '#f9f9fb',
  inputBg: isDark ? '#1e1e2e' : '#ffffff',
  accent: '#056EE9',
  danger: '#f1416c',
})
type Tokens = ReturnType<typeof makeTokens>

const inputStyle = (t: Tokens): React.CSSProperties => ({
  background: t.inputBg,
  color: t.text,
  border: `1px solid ${t.border}`,
  borderRadius: 10,
  padding: '0.6rem 0.75rem',
  outline: 'none',
  width: '100%',
  fontSize: '0.95rem',
})

const labelStyle = (t: Tokens): React.CSSProperties => ({
  color: t.muted,
  fontSize: '0.8rem',
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  marginBottom: '0.4rem',
  display: 'block',
})

const helperStyle = (t: Tokens): React.CSSProperties => ({
  color: t.muted,
  fontSize: '0.875rem',
  lineHeight: 1.6,
  marginBottom: 0,
})

const errorStyle = (t: Tokens): React.CSSProperties => ({
  color: t.danger,
  fontSize: '0.875rem',
  fontWeight: 600,
  marginBottom: 0,
})

// Dark mode goes white-on-translucent: the blue-on-blue variant is unreadable
// over the sheet's dark gradient.
const chipStyle = (t: Tokens): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.4rem',
  padding: '0.35rem 0.75rem',
  borderRadius: 999,
  border: t.isDark ? '1.5px solid rgba(255,255,255,0.45)' : `1.5px solid ${t.accent}`,
  background: t.isDark ? 'rgba(255,255,255,0.08)' : 'rgba(5,110,233,0.08)',
  color: t.isDark ? '#ffffff' : t.accent,
  fontSize: '0.9rem',
  fontWeight: 600,
})

/** Muted micro-caps header cells above the timings/breaks lists. */
const listHeaderCellStyle = (t: Tokens): React.CSSProperties => ({
  color: t.muted,
  fontWeight: 700,
  fontSize: '0.72rem',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
})

/** Accent-tinted badge showing the row's day name. */
const dayBadgeStyle = (t: Tokens): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0.3rem 0.55rem',
  borderRadius: 8,
  background: 'rgba(5,110,233,0.1)',
  color: t.isDark ? '#6ea8f7' : t.accent,
  fontWeight: 700,
  fontSize: '0.78rem',
  whiteSpace: 'nowrap',
})

/** Quiet outlined pill (lecture duration, counts). */
const pillStyle = (t: Tokens): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '0.2rem 0.55rem',
  borderRadius: 999,
  border: `1px solid ${t.border}`,
  color: t.muted,
  fontSize: '0.78rem',
  fontWeight: 600,
  whiteSpace: 'nowrap',
})

/** Round day toggle, shared by the timings and breaks forms. */
const DayChip: FC<{
  label: string
  selected: boolean
  disabled: boolean
  title?: string
  onToggle: () => void
  tokens: Tokens
}> = ({label, selected, disabled, title, onToggle, tokens: t}) => (
  <button
    type='button'
    onClick={onToggle}
    disabled={disabled}
    title={title}
    style={{
      width: 38,
      height: 38,
      borderRadius: '50%',
      border: `2px solid ${selected ? t.accent : t.border}`,
      background: selected ? t.accent : 'transparent',
      color: selected ? '#ffffff' : disabled ? `${t.muted}80` : t.muted,
      opacity: disabled ? 0.5 : 1,
      cursor: disabled ? 'not-allowed' : 'pointer',
      fontWeight: 600,
      fontSize: '0.85rem',
    }}
  >
    {label}
  </button>
)

/**
 * Cancel (left) / Save (right) footer, flowing directly under the section's
 * content — same layout as the wizard's Back/Next. Sticky keeps it reachable
 * while long content scrolls. Styled by .step2-edit-sheet-* in
 * _onboardingwizard.scss. Every editor keeps Save disabled until its draft
 * differs from what the sheet opened with, so an untouched sheet cannot commit.
 */
const SheetFooter: FC<{
  onCancel: () => void
  onSave: () => void
  saveDisabled: boolean
  isSubmitting: boolean
  tokens: Tokens
  intl: IntlShape
  /** Drop the Save button entirely (nothing to commit yet); Cancel stays. */
  hideSave?: boolean
}> = ({onCancel, onSave, saveDisabled, isSubmitting, intl, hideSave = false}) => (
  <div className='step2-edit-sheet-footer'>
    <button
      type='button'
      className='step2-edit-sheet-cancel'
      onClick={onCancel}
      disabled={isSubmitting}
    >
      {intl.formatMessage({id: 'GENERAL.CANCEL'})}
    </button>
    {!hideSave && (
      <button
        type='button'
        className='step2-edit-sheet-save'
        onClick={onSave}
        disabled={saveDisabled || isSubmitting}
      >
        {isSubmitting && <span className='spinner-border spinner-border-sm align-middle me-2' />}
        {intl.formatMessage({id: 'GENERAL.SAVE'})}
      </button>
    )}
  </div>
)

/** Root layout every section editor shares. */
const editorRootProps = {
  className: 'd-flex flex-column gap-4 pt-4',
}

/**
 * Per-section "don't ask again" persistence for the delete confirmation.
 * Stored per editor section so skipping in Timings does not silence Breaks.
 */
const skipDeleteConfirmKey = (section: string) => `onboarding_step2_skip_delete_confirm:${section}`
const readSkipDeleteConfirm = (section: string): boolean => {
  try {
    return localStorage.getItem(skipDeleteConfirmKey(section)) === 'true'
  } catch {
    return false
  }
}
const writeSkipDeleteConfirm = (section: string) => {
  try {
    localStorage.setItem(skipDeleteConfirmKey(section), 'true')
  } catch {
    // Storage unavailable — the dialog simply keeps asking.
  }
}

/**
 * Delete confirmation, styled to the sheet (the shared MUI ConfirmationModal
 * carries hardcoded English and a different design language). Portals above the
 * sheet's z-index; backdrop click or Cancel dismisses. `onConfirm` receives the
 * "do not show again" checkbox state so the host can stop asking.
 */
const ConfirmDeleteDialog: FC<{
  open: boolean
  onCancel: () => void
  onConfirm: (dontShowAgain: boolean) => void
  tokens: Tokens
  intl: IntlShape
}> = ({open, onCancel, onConfirm, tokens: t, intl}) => {
  const [dontShow, setDontShow] = useState(false)
  const checkboxId = useId()
  React.useEffect(() => {
    if (open) setDontShow(false)
  }, [open])
  if (!open) return null
  return createPortal(
    <div
      role='dialog'
      aria-modal='true'
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1080,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        onClick={onCancel}
        style={{position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)'}}
      />
      <div
        style={{
          position: 'relative',
          width: 'min(360px, calc(100vw - 2rem))',
          borderRadius: 14,
          border: `1px solid ${t.border}`,
          background: t.isDark ? '#1a1b23' : '#ffffff',
          padding: '1.4rem',
          boxShadow: '0 16px 48px rgba(0,0,0,0.35)',
        }}
      >
        <div className='d-flex align-items-center gap-2 mb-2'>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 34,
              height: 34,
              borderRadius: 10,
              background: 'rgba(241,65,108,0.12)',
              color: t.danger,
              flexShrink: 0,
            }}
          >
            <Trash2 size={16} />
          </span>
          <span style={{color: t.text, fontWeight: 700, fontSize: '1rem'}}>
            {intl.formatMessage({id: 'MENU.ARE_YOU_SURE?'})}
          </span>
        </div>
        <p style={{...helperStyle(t), marginBottom: '0.9rem'}}>
          {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.DELETE_ROW_CONFIRM'})}
        </p>
        <label
          htmlFor={checkboxId}
          className='d-flex align-items-center gap-2'
          style={{cursor: 'pointer', marginBottom: '1.1rem', userSelect: 'none'}}
        >
          <input
            id={checkboxId}
            type='checkbox'
            className='form-check-input step2-delete-confirm-check'
            checked={dontShow}
            onChange={(e) => setDontShow(e.target.checked)}
            style={{width: 16, height: 16, marginTop: 0, cursor: 'pointer', flexShrink: 0}}
          />
          <span style={{color: t.muted, fontSize: '0.85rem'}}>
            {intl.formatMessage({id: 'ALERT.DO_NOT_SHOW_AGAIN'})}
          </span>
        </label>
        <div className='d-flex justify-content-end gap-2'>
          <button
            type='button'
            className='step2-edit-sheet-cancel step2-edit-sheet-btn-sm'
            onClick={onCancel}
          >
            {intl.formatMessage({id: 'GENERAL.CANCEL'})}
          </button>
          <button
            type='button'
            onClick={() => onConfirm(dontShow)}
            style={{
              border: 'none',
              borderRadius: 10,
              padding: '0.45rem 1.1rem',
              fontWeight: 600,
              fontSize: '0.875rem',
              color: '#ffffff',
              background: t.danger,
              boxShadow: '0 6px 16px rgba(241,65,108,0.35)',
            }}
          >
            {intl.formatMessage({id: 'GENERAL.DELETE'})}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

/**
 * The Institution Setup review page's edit-sheet content: a compact, sheet-native
 * editor per section. Unlike the old modal (which embedded the whole
 * Step2BasicSetup flow through CSS scope hacks), each editor here works on a
 * LOCAL DRAFT seeded from the wizard state and commits it in one `updateData`
 * call on Save — closing without saving discards the draft. Data contracts,
 * validation rules and i18n keys mirror Step2BasicSetup exactly, so both UIs
 * stay interchangeable; Step2BasicSetup itself is untouched and still serves
 * the flows that embed it elsewhere.
 */
const Step2SectionEditor: FC<Props> = ({
  section,
  data,
  updateData,
  isDark,
  onDone,
  onPartialSubmit,
  isSubmitting = false,
}) => {
  const intl = useIntl()
  const t = makeTokens(isDark)
  const md = data.manualData

  /** Merge the section's fields into manualData as ONE user edit, then close. */
  const commitAndClose = (fields: Partial<ManualData>, opts?: {syncBranches?: boolean}) => {
    const merged = {...md, ...fields}
    updateData({manualData: merged})
    if (opts?.syncBranches && onPartialSubmit) {
      onPartialSubmit(['branches'], merged, onDone)
    } else {
      onDone()
    }
  }

  const common = {intl, tokens: t, onCancel: onDone, isSubmitting}

  switch (section) {
    case 'branches':
      return (
        <BranchesEditor
          {...common}
          // Names only: state persisted by an older build may still hold `{name, id}` entries.
          initial={((md?.branches ?? []) as unknown[])
            .map((entry: any) =>
              String(typeof entry === 'string' ? entry : (entry?.name ?? '')).trim()
            )
            .filter(Boolean)}
          isSaved={(name) => branchIdOf(md, name) !== null}
          isFirst={(name) => isFirstBranch(md, name)}
          onSave={(branches, renames) => {
            // Renames keep the branch's server id (sent as an update); a saved branch left out
            // of the list is queued for delete, its classes and allocations dropped with it.
            const renamed = renames.reduce((acc, [from, to]) => renameBranch(acc, from, to), md)
            const kept = new Set(branches.map((name) => name.toLowerCase()))
            const removed = ((renamed?.branches ?? []) as unknown[])
              .map((entry: any) =>
                String(typeof entry === 'string' ? entry : (entry?.name ?? '')).trim()
              )
              .filter((name) => name && !kept.has(name.toLowerCase()))
            commitAndClose({...removeBranches(renamed, removed), branches}, {syncBranches: true})
          }}
        />
      )
    case 'duration':
      return (
        <SessionEditor
          {...common}
          initial={{
            name: md?.durationName ?? '',
            start: md?.durationStartDate ?? '',
            end: md?.durationEndDate ?? '',
          }}
          onSave={({name, start, end}) =>
            commitAndClose({durationName: name, durationStartDate: start, durationEndDate: end})
          }
        />
      )
    case 'frequency':
      return (
        <FrequencyEditor
          {...common}
          initial={{
            count: md?.defaultSubjectFrequency ?? 0,
            per: md?.defaultFrequencyPer ?? 'week',
          }}
          onSave={({count, per}) =>
            commitAndClose({defaultSubjectFrequency: count, defaultFrequencyPer: per})
          }
        />
      )
    case 'venues':
      return (
        <VenuesEditor
          {...common}
          initial={{
            same: md?.venuesSameForClass ?? null,
            venues: md?.venues ?? [],
            autoCount: md?.venuesAutoCount ?? 0,
          }}
          onSave={({same, venues, autoCount}) => {
            // Same side effect the legacy count handler kept for the seeder.
            if (same === false && autoCount > 0) {
              localStorage.setItem('onboarding_venues_auto_count', String(autoCount))
            } else {
              localStorage.removeItem('onboarding_venues_auto_count')
            }
            commitAndClose({
              venuesSameForClass: same,
              venues: same === false ? venues : [],
              venuesCount: same === false ? venues.length : 0,
              venuesAutoCount: same === false ? autoCount : 0,
            })
          }}
        />
      )
    case 'timings': {
      const breaks = ((md as any)?.break_timings ?? []) as Array<{day?: string}>
      return (
        <TimingsEditor
          {...common}
          initial={(md?.timings ?? []) as TimingRow[]}
          breakDays={Array.from(new Set(breaks.map((b) => normalizeDay(b?.day))))}
          onSave={(timings) => {
            // A break belongs to its day's school hours, so a day dropped from the timings
            // takes its breaks with it (the editor warned about them). Days that keep timings
            // keep their breaks untouched, even when the hours changed.
            const kept = new Set(timings.map((row) => normalizeDay(row.day)))
            const nextBreaks = breaks.filter((b) => kept.has(normalizeDay(b?.day)))
            commitAndClose({
              timings: timings as ManualData['timings'],
              ...(nextBreaks.length !== breaks.length
                ? ({break_timings: nextBreaks} as Partial<ManualData>)
                : {}),
            })
          }}
        />
      )
    }
    case 'breaks':
      return (
        <BreaksEditor
          {...common}
          initial={((md as any)?.break_timings ?? []).map((b: any) => ({
            day: String(b.day ?? 'MONDAY'),
            startTime: b.startTime ?? '',
            endTime: b.endTime ?? '',
          }))}
          timings={(md?.timings ?? []) as TimingRow[]}
          onSave={(break_timings) => commitAndClose({break_timings} as Partial<ManualData>)}
        />
      )
    default:
      return null
  }
}

interface EditorCommon {
  intl: IntlShape
  tokens: Tokens
  onCancel: () => void
  isSubmitting: boolean
}

/* ------------------------------- Branches ---------------------------------- */

const BranchesEditor: FC<
  EditorCommon & {
    initial: string[]
    /** `renames` are `[from, to]` pairs in the order they were made. */
    onSave: (branches: string[], renames: Array<[string, string]>) => void
    /** The branch is on the server: removing it deletes its data, so it asks first. */
    isSaved: (name: string) => boolean
    /** The school's first branch: it can't be deleted, only renamed. */
    isFirst: (name: string) => boolean
  }
> = ({intl, tokens: t, onCancel, isSubmitting, initial, onSave, isSaved, isFirst}) => {
  const [branches, setBranches] = useState<string[]>(initial)
  const [renames, setRenames] = useState<Array<[string, string]>>([])
  const [request, setRequest] = useState<BranchRemovalRequest | null>(null)
  const [input, setInput] = useState('')
  const [error, setError] = useState<string | null>(null)
  const inputId = useId()

  // A chip renamed in this sheet still is the saved branch it started as.
  const savedNameOf = (name: string) => {
    let current = name
    for (let i = renames.length - 1; i >= 0; i--) {
      if (renames[i][1] === current) current = renames[i][0]
    }
    return current
  }
  const requestRemove = (name: string) => {
    const saved = savedNameOf(name)
    if (isFirst(saved)) setRequest({name, mode: 'rename'})
    else if (isSaved(saved)) setRequest({name, mode: 'remove'})
    else setBranches((prev) => prev.filter((b) => b !== name))
  }

  // A name still typed in the box counts as a change: Save adds it.
  const dirty = Boolean(input.trim()) || !sameJson(branches, initial)

  const tryAdd = (): boolean => {
    const name = input.trim()
    if (!name) return true
    // Same rule as Step2BasicSetup: letters, numbers, spaces and hyphens only.
    if (!/^[\p{L}\p{N}\s-]+$/u.test(name)) {
      setError('Branch name cannot contain special characters.')
      return false
    }
    if (branches.some((b) => b.toLowerCase() === name.toLowerCase())) {
      setError(intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.BRANCH_DUPLICATE_ERROR'}))
      setInput('')
      return false
    }
    setError(null)
    setBranches((prev) => [...prev, name])
    setInput('')
    return true
  }

  return (
    <div {...editorRootProps}>
      <p style={helperStyle(t)}>
        {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.BRANCHES_INSTRUCTION'})}
      </p>
      <div>
        <label style={labelStyle(t)} htmlFor={inputId}>
          {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.BRANCHES'})}
        </label>
        <div
          onClick={() => document.getElementById(inputId)?.focus()}
          style={{
            ...inputStyle(t),
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '0.5rem',
            cursor: 'text',
            padding: '0.5rem 0.75rem',
          }}
        >
          {branches.map((name, idx) => (
            <span key={`${name}-${idx}`} style={chipStyle(t)}>
              {name}
              {(branches.length === 1 || name !== DEFAULT_BRANCH_NAME) && (
                <button
                  type='button'
                  aria-label='Remove'
                  className='step2-edit-sheet-chip-remove'
                  onClick={(e) => {
                    e.stopPropagation()
                    requestRemove(name)
                  }}
                >
                  <X size={13} strokeWidth={3} />
                </button>
              )}
            </span>
          ))}
          <input
            id={inputId}
            type='text'
            value={input}
            onChange={(e) => {
              setInput(e.target.value)
              if (error) setError(null)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ',') {
                e.preventDefault()
                tryAdd()
              } else if (e.key === 'Backspace' && input === '' && branches.length > 0) {
                // Same rule as the chip's × button: the default branch only
                // goes when it is the last one standing.
                const last = branches[branches.length - 1]
                if (branches.length === 1 || last !== DEFAULT_BRANCH_NAME) {
                  e.preventDefault()
                  requestRemove(last)
                }
              }
            }}
            placeholder={intl.formatMessage({
              id: 'ONBOARDING_WIZARD.STEP2.BRANCHES_INPUT_PLACEHOLDER',
            })}
            style={{
              border: 'none',
              outline: 'none',
              background: 'transparent',
              flex: 1,
              minWidth: 120,
              color: t.text,
              fontSize: '0.95rem',
            }}
          />
        </div>
        {error && (
          <p className='mt-2' style={errorStyle(t)}>
            {error}
          </p>
        )}
      </div>
      <p style={helperStyle(t)}>
        {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.BRANCHES_SINGLE_CAMPUS_NOTE'})}
      </p>
      <SheetFooter
        onCancel={onCancel}
        onSave={() => {
          // A name still sitting in the input counts — validate and include it.
          const name = input.trim()
          let final = branches
          if (name) {
            if (!/^[\p{L}\p{N}\s-]+$/u.test(name)) {
              setError('Branch name cannot contain special characters.')
              return
            }
            if (!branches.some((b) => b.toLowerCase() === name.toLowerCase())) {
              final = [...branches, name]
            }
          }
          if (final.length > 0) onSave(final, renames)
        }}
        saveDisabled={!dirty || (branches.length === 0 && !input.trim())}
        isSubmitting={isSubmitting}
        tokens={t}
        intl={intl}
      />
      <BranchRemovalDialog
        request={request}
        branches={branches}
        isDark={t.isDark}
        onCancel={() => setRequest(null)}
        onRemove={(name) => {
          setBranches((prev) => prev.filter((b) => b !== name))
          setRequest(null)
        }}
        onRename={(from, to) => {
          setBranches((prev) => prev.map((b) => (b === from ? to : b)))
          setRenames((prev) => [...prev, [from, to]])
          setRequest(null)
        }}
      />
    </div>
  )
}

/* ------------------------------- Session ----------------------------------- */

const SessionEditor: FC<
  EditorCommon & {
    initial: {name: string; start: string; end: string}
    onSave: (v: {name: string; start: string; end: string}) => void
  }
> = ({intl, tokens: t, onCancel, isSubmitting, initial, onSave}) => {
  const [name, setName] = useState(initial.name)
  const [start, setStart] = useState(initial.start)
  const [end, setEnd] = useState(initial.end)
  const nameId = useId()
  const startId = useId()
  const endId = useId()

  const rangeInvalid = Boolean(start && end && end <= start)
  const valid = Boolean(name.trim() && start && end && !rangeInvalid)
  const dirty =
    name.trim() !== initial.name.trim() || start !== initial.start || end !== initial.end

  const dateField = (
    id: string,
    labelId: string,
    value: string,
    onChange: (v: string) => void,
    min: string
  ) => (
    <div style={{flex: 1, minWidth: 150}}>
      <label style={labelStyle(t)} htmlFor={id}>
        {intl.formatMessage({id: labelId as any})}
      </label>
      <WizardDatePicker
        id={id}
        min={min}
        max={SESSION_DATE_MAX}
        value={value}
        onChange={onChange}
        ariaLabel={intl.formatMessage({id: labelId as any})}
        style={{background: t.inputBg, color: t.text, border: `1px solid ${t.border}`}}
      />
    </div>
  )

  return (
    <div {...editorRootProps}>
      <p style={helperStyle(t)}>
        {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.SESSION_HELPER'})}
      </p>
      <div>
        <label style={labelStyle(t)} htmlFor={nameId}>
          {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.SESSION_NAME_LABEL'})}
        </label>
        <input
          id={nameId}
          type='text'
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={intl.formatMessage({
            id: 'ONBOARDING_WIZARD.STEP2.DURATION_NAME_PLACEHOLDER',
          })}
          style={inputStyle(t)}
        />
      </div>
      <div className='d-flex gap-3 flex-wrap'>
        {dateField(
          startId,
          'ONBOARDING_WIZARD.STEP2.SESSION_START_DATE_LABEL',
          start,
          setStart,
          SESSION_DATE_MIN
        )}
        {dateField(
          endId,
          'ONBOARDING_WIZARD.STEP2.SESSION_END_DATE_LABEL',
          end,
          setEnd,
          start || SESSION_DATE_MIN
        )}
      </div>
      {rangeInvalid && (
        <p style={errorStyle(t)}>
          {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.SESSION_END_AFTER_START'})}
        </p>
      )}
      <p style={helperStyle(t)}>
        {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.SESSION_INFO'})}
      </p>
      <SheetFooter
        onCancel={onCancel}
        onSave={() => valid && onSave({name: name.trim(), start, end})}
        saveDisabled={!valid || !dirty}
        isSubmitting={isSubmitting}
        tokens={t}
        intl={intl}
      />
    </div>
  )
}

/* ------------------------------ Frequency ---------------------------------- */

const FrequencyEditor: FC<
  EditorCommon & {
    initial: {count: number; per: 'week' | 'day'}
    onSave: (v: {count: number; per: 'week' | 'day'}) => void
  }
> = ({intl, tokens: t, onCancel, isSubmitting, initial, onSave}) => {
  const [count, setCount] = useState(initial.count)
  const [per, setPer] = useState<'week' | 'day'>(initial.per)
  const dirty = count !== initial.count || per !== initial.per

  const stepBtn = (delta: number, label: string) => (
    <button
      type='button'
      aria-label={label}
      onClick={() => setCount((c) => Math.max(1, Math.min(20, (c || 0) + delta)))}
      style={{
        width: 40,
        height: 40,
        borderRadius: 10,
        border: `1px solid ${t.border}`,
        background: t.inputBg,
        color: t.text,
        fontSize: '1.2rem',
        fontWeight: 700,
        lineHeight: 1,
      }}
    >
      {label}
    </button>
  )

  const segBtn = (value: 'week' | 'day', labelId: string) => (
    <button
      type='button'
      onClick={() => setPer(value)}
      aria-pressed={per === value}
      style={{
        flex: 1,
        padding: '0.55rem 1rem',
        borderRadius: 8,
        border: 'none',
        background: per === value ? t.accent : 'transparent',
        color: per === value ? '#ffffff' : t.muted,
        fontWeight: 600,
        fontSize: '0.9rem',
        transition: 'all 0.15s ease',
      }}
    >
      {intl.formatMessage({id: labelId as any})}
    </button>
  )

  return (
    <div {...editorRootProps}>
      <p style={helperStyle(t)}>
        {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.FREQUENCY_INSTRUCTION'})}
      </p>
      <div className='d-flex align-items-center gap-3 flex-wrap'>
        {stepBtn(-1, '−')}
        <div
          style={{
            minWidth: 64,
            textAlign: 'center',
            fontSize: '1.6rem',
            fontWeight: 700,
            color: t.text,
          }}
        >
          {count || '—'}
        </div>
        {stepBtn(1, '+')}
        <span style={{color: t.muted}}>
          {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_PER'})}
        </span>
        <div
          className='d-flex'
          style={{
            border: `1px solid ${t.border}`,
            borderRadius: 10,
            padding: 3,
            background: t.inputBg,
            minWidth: 190,
          }}
        >
          {segBtn('week', 'ONBOARDING_WIZARD.STEP2.TIMINGS_FREQUENCY_PER_WEEK')}
          {segBtn('day', 'ONBOARDING_WIZARD.STEP2.TIMINGS_FREQUENCY_PER_DAY')}
        </div>
      </div>
      <SheetFooter
        onCancel={onCancel}
        onSave={() => count >= 1 && count <= 20 && onSave({count, per})}
        saveDisabled={!dirty || count < 1 || count > 20}
        isSubmitting={isSubmitting}
        tokens={t}
        intl={intl}
      />
    </div>
  )
}

/* -------------------------------- Venues ----------------------------------- */

const VenuesEditor: FC<
  EditorCommon & {
    initial: {same: boolean | null; venues: Array<{name: string}>; autoCount: number}
    onSave: (v: {same: boolean | null; venues: Array<{name: string}>; autoCount: number}) => void
  }
> = ({intl, tokens: t, onCancel, isSubmitting, initial, onSave}) => {
  const [same, setSame] = useState<boolean | null>(initial.same)
  const [venues, setVenues] = useState(initial.venues)
  const [autoCount, setAutoCount] = useState(initial.autoCount)
  const [input, setInput] = useState('')
  const manualInputId = useId()

  // One entry mode at a time (the legacy section greyed the other input out
  // instead); both drafts survive switching, Save commits only the active one.
  const [entryMode, setEntryMode] = useState<'auto' | 'manual'>(
    initial.venues.length > 0 ? 'manual' : 'auto'
  )

  // Compare what Save would commit against what the sheet opened with. "Same
  // venues" drops the list and count either way, so both sides normalise to that.
  const commitShape = (v: {
    same: boolean | null
    venues: Array<{name: string}>
    autoCount: number
  }) =>
    v.same === false
      ? {same: false, venues: v.venues.map((x) => x.name), autoCount: v.autoCount}
      : {same: v.same, venues: [], autoCount: 0}
  const dirty =
    Boolean(input.trim()) ||
    !sameJson(
      commitShape({
        same,
        venues: entryMode === 'manual' ? venues : [],
        autoCount: entryMode === 'auto' ? autoCount : 0,
      }),
      commitShape(initial)
    )

  const optionCard = (value: boolean, titleId: string, descId: string) => {
    const selected = same === value
    return (
      <button
        type='button'
        onClick={() => setSame(value)}
        aria-pressed={selected}
        style={{
          flex: 1,
          minWidth: 200,
          textAlign: 'left',
          borderRadius: 12,
          padding: '1rem 1.1rem',
          border: `2px solid ${selected ? t.accent : t.border}`,
          background: selected ? 'rgba(5,110,233,0.06)' : 'transparent',
          transition: 'all 0.2s ease',
        }}
      >
        <div style={{color: t.text, fontWeight: 700, fontSize: '0.98rem', marginBottom: '0.25rem'}}>
          {intl.formatMessage({id: titleId as any})}
        </div>
        <div style={{color: t.muted, fontSize: '0.85rem', lineHeight: 1.5}}>
          {intl.formatMessage({id: descId as any})}
        </div>
      </button>
    )
  }

  const addVenue = () => {
    const name = input.trim()
    if (!name) return
    if (venues.some((v) => v.name === name)) {
      setInput('')
      return
    }
    setVenues((prev) => [...prev, {name}])
    setInput('')
  }

  return (
    <div {...editorRootProps}>
      <p style={helperStyle(t)}>
        {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.VENUES_QUESTION'})}
      </p>
      <div className='d-flex gap-3 flex-wrap'>
        {optionCard(
          true,
          'ONBOARDING_WIZARD.STEP2.VENUES_SAME',
          'ONBOARDING_WIZARD.STEP2.VENUES_SAME_DESC'
        )}
        {optionCard(
          false,
          'ONBOARDING_WIZARD.STEP2.VENUES_DIFFERENT',
          'ONBOARDING_WIZARD.STEP2.VENUES_DIFFERENT_DESC'
        )}
      </div>
      <p style={helperStyle(t)}>
        {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.VENUES_APPLIED_INFO'})}{' '}
        {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.VENUES_APPLIED_CONSTRAINTS'})}
      </p>

      {same === false && (
        <div
          className='d-flex flex-column gap-3'
          style={{
            border: `1px solid ${t.border}`,
            borderRadius: 14,
            padding: '1.1rem 1.2rem',
            background: t.panelBg,
          }}
        >
          <div className='d-flex align-items-center gap-2'>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 30,
                height: 30,
                borderRadius: 8,
                background: 'rgba(5,110,233,0.1)',
                color: t.isDark ? '#6ea8f7' : t.accent,
                flexShrink: 0,
              }}
            >
              <MapPin size={15} />
            </span>
            <span style={{color: t.text, fontWeight: 700, fontSize: '0.95rem', flex: 1}}>
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.VENUES_LIST_TITLE'})}
            </span>
            {(entryMode === 'manual' ? venues.length : autoCount) > 0 && (
              <span style={pillStyle(t)}>
                {intl.formatMessage(
                  {id: 'ONBOARDING_WIZARD.STEP2.VENUES_COUNT_LABEL'},
                  {count: entryMode === 'manual' ? venues.length : autoCount}
                )}
              </span>
            )}
          </div>

          <div
            className='d-flex'
            role='tablist'
            style={{
              border: `1px solid ${t.border}`,
              borderRadius: 10,
              padding: 3,
              background: t.inputBg,
            }}
          >
            {(
              [
                ['auto', Sparkles, 'ONBOARDING_WIZARD.STEP2.VENUES_MODE_AUTO'],
                ['manual', Keyboard, 'ONBOARDING_WIZARD.STEP2.VENUES_MODE_MANUAL'],
              ] as const
            ).map(([value, Icon, labelId]) => (
              <button
                key={value}
                type='button'
                role='tab'
                aria-selected={entryMode === value}
                onClick={() => setEntryMode(value)}
                className='d-inline-flex align-items-center justify-content-center gap-2'
                style={{
                  flex: 1,
                  padding: '0.55rem 1rem',
                  borderRadius: 8,
                  border: 'none',
                  background: entryMode === value ? t.accent : 'transparent',
                  color: entryMode === value ? '#ffffff' : t.muted,
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  transition: 'all 0.15s ease',
                }}
              >
                <Icon size={15} />
                {intl.formatMessage({id: labelId})}
              </button>
            ))}
          </div>

          {entryMode === 'auto' ? (
            <div className='d-flex flex-column gap-3'>
              <p style={helperStyle(t)}>
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.VENUES_MODE_AUTO_DESC'})}
              </p>
              <div className='d-flex align-items-center gap-3 flex-wrap'>
                {([-1, 1] as const).map((delta) => (
                  <button
                    key={delta}
                    type='button'
                    aria-label={delta === 1 ? 'Increase' : 'Decrease'}
                    onClick={() => setAutoCount((c) => Math.max(0, Math.min(999, c + delta)))}
                    disabled={delta === -1 ? autoCount <= 0 : autoCount >= 999}
                    style={{
                      width: 38,
                      height: 38,
                      borderRadius: 10,
                      border: `1px solid ${t.border}`,
                      background: t.inputBg,
                      color: t.text,
                      fontWeight: 700,
                      fontSize: '1.1rem',
                      order: delta === 1 ? 2 : 0,
                    }}
                  >
                    {delta === 1 ? '+' : '−'}
                  </button>
                ))}
                <input
                  type='number'
                  min={0}
                  max={999}
                  value={autoCount || ''}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10)
                    setAutoCount(isNaN(v) ? 0 : Math.max(0, Math.min(999, v)))
                  }}
                  placeholder={intl.formatMessage({
                    id: 'ONBOARDING_WIZARD.STEP2.VENUES_COUNT_PLACEHOLDER',
                  })}
                  style={{...inputStyle(t), width: 170, textAlign: 'center', order: 1}}
                />
              </div>
              {autoCount > 0 && (
                <div className='d-flex flex-wrap gap-2'>
                  {Array.from({length: Math.min(autoCount, 6)}, (_, i) => (
                    <span key={i} style={pillStyle(t)}>
                      Venue {i + 1}
                    </span>
                  ))}
                  {autoCount > 6 && <span style={pillStyle(t)}>+{autoCount - 6}</span>}
                </div>
              )}
            </div>
          ) : (
            <div className='d-flex flex-column gap-3'>
              <p style={helperStyle(t)}>
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.VENUES_MODE_MANUAL_DESC'})}
              </p>
              <div
                onClick={() => document.getElementById(manualInputId)?.focus()}
                style={{
                  ...inputStyle(t),
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: '0.5rem',
                  cursor: 'text',
                  padding: '0.5rem 0.75rem',
                }}
              >
                {venues.map((v, idx) => (
                  <span key={`${v.name}-${idx}`} style={chipStyle(t)}>
                    {v.name}
                    <button
                      type='button'
                      aria-label='Remove'
                      className='step2-edit-sheet-chip-remove'
                      onClick={(e) => {
                        e.stopPropagation()
                        setVenues((prev) => prev.filter((_, i) => i !== idx))
                      }}
                    >
                      <X size={13} strokeWidth={3} />
                    </button>
                  </span>
                ))}
                <input
                  id={manualInputId}
                  type='text'
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ',') {
                      e.preventDefault()
                      addVenue()
                    } else if (e.key === 'Backspace' && input === '' && venues.length > 0) {
                      e.preventDefault()
                      setVenues((prev) => prev.slice(0, -1))
                    }
                  }}
                  onBlur={addVenue}
                  style={{
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    flex: 1,
                    minWidth: 120,
                    color: t.text,
                    fontSize: '0.95rem',
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      <SheetFooter
        onCancel={onCancel}
        onSave={() =>
          same !== null &&
          onSave({
            same,
            venues: entryMode === 'manual' ? venues : [],
            autoCount: entryMode === 'auto' ? autoCount : 0,
          })
        }
        saveDisabled={!dirty || same === null}
        isSubmitting={isSubmitting}
        tokens={t}
        intl={intl}
      />
    </div>
  )
}

/* -------------------------------- Timings ---------------------------------- */

const TimingsEditor: FC<
  EditorCommon & {
    initial: TimingRow[]
    /** Normalised days that currently have a saved break, for the removal warning. */
    breakDays: string[]
    onSave: (timings: TimingRow[]) => void
  }
> = ({intl, tokens: t, onCancel, isSubmitting, initial, breakDays, onSave}) => {
  const [rows, setRows] = useState<TimingRow[]>(initial)
  // null = form closed; -1 = adding; BULK_EDIT = editing all rows at once;
  // >= 0 = editing that row
  const BULK_EDIT = -2
  const [formFor, setFormFor] = useState<number | null>(initial.length === 0 ? -1 : null)
  // Row index awaiting delete confirmation, or null.
  const [confirmDeleteIdx, setConfirmDeleteIdx] = useState<number | null>(null)
  // "Do not show again" ticked earlier (persisted): delete rows without asking.
  const [skipDeleteConfirm, setSkipDeleteConfirm] = useState(() => readSkipDeleteConfirm('timings'))

  const deleteRow = (idx: number) => {
    setRows((prev) => prev.filter((_, i) => i !== idx))
    if (formFor === idx) setFormFor(null)
  }
  const [form, setForm] = useState({
    selectedDays: [] as string[],
    startTime: DEFAULT_TIMING_START,
    endTime: DEFAULT_TIMING_END,
    lectureDuration: DEFAULT_LECTURE_DURATION,
  })
  const [formError, setFormError] = useState<string | null>(null)

  const usedDays = useMemo(() => new Set(rows.map((r) => normalizeDay(r.day))), [rows])

  const openAdd = () => {
    setForm({
      selectedDays: DEFAULT_TIMING_DAYS.filter((d) => !usedDays.has(normalizeDay(d))),
      startTime: DEFAULT_TIMING_START,
      endTime: DEFAULT_TIMING_END,
      lectureDuration: DEFAULT_LECTURE_DURATION,
    })
    setFormError(null)
    setFormFor(-1)
  }

  const openEdit = (idx: number) => {
    const r = rows[idx]
    setForm({
      selectedDays: [r.day],
      startTime: r.startTime,
      endTime: r.endTime,
      lectureDuration: r.lectureDuration ?? 0,
    })
    setFormError(null)
    setFormFor(idx)
  }

  /**
   * Bulk edit: one form pre-selecting every current day, whose Save REPLACES the
   * whole list — one time span and lecture duration applied to all selected days.
   * Only days that already have a row are toggleable (so a day can be dropped,
   * but new days such as Saturday/Sunday are added via "Add", not here).
   * Seeded from the first row.
   */
  const openBulkEdit = () => {
    const first = rows[0]
    setForm({
      selectedDays: rows.map((r) => normalizeDay(r.day)),
      startTime: first?.startTime ?? DEFAULT_TIMING_START,
      endTime: first?.endTime ?? DEFAULT_TIMING_END,
      lectureDuration: first?.lectureDuration ?? DEFAULT_LECTURE_DURATION,
    })
    setFormError(null)
    setFormFor(BULK_EDIT)
  }

  // Bulk mode enables only days that already have a row; edit mode locks the
  // chips to the edited row's day; add mode disables days that already have a
  // row — same rules as the legacy timing modal.
  const isDayDisabled = (day: string) => {
    if (formFor === BULK_EDIT) return !usedDays.has(normalizeDay(day))
    if (formFor !== null && formFor >= 0) {
      return normalizeDay(day) !== normalizeDay(rows[formFor]?.day)
    }
    return usedDays.has(normalizeDay(day))
  }

  const applyForm = () => {
    const {selectedDays, startTime, endTime, lectureDuration} = form
    if (!startTime || !endTime || selectedDays.length === 0) return
    if (timeToMinutes(endTime) - timeToMinutes(startTime) < (lectureDuration ?? 0)) {
      setFormError(intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_SPAN_TOO_SHORT'}))
      return
    }
    // Bulk replaces the entire list, so no day can collide with a kept row.
    if (formFor !== BULK_EDIT) {
      const otherDays = new Set<string>()
      rows.forEach((r, idx) => {
        if (formFor !== null && idx === formFor) return
        otherDays.add(normalizeDay(r.day))
      })
      if (selectedDays.some((d) => otherDays.has(normalizeDay(d)))) {
        setFormError(intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_DUPLICATE_ERROR'}))
        return
      }
    }
    const entries = selectedDays.map((day) => ({day, startTime, endTime, lectureDuration}))
    if (formFor === BULK_EDIT) {
      // Keep weekday order for the rebuilt list.
      setRows(
        WEEKDAYS.filter((d) => selectedDays.includes(d)).map((day) => ({
          day,
          startTime,
          endTime,
          lectureDuration,
        }))
      )
    } else if (formFor !== null && formFor >= 0) {
      setRows((prev) => {
        const next = [...prev]
        next.splice(formFor, 1, ...entries)
        return next
      })
    } else {
      setRows((prev) => [...prev, ...entries])
    }
    setFormError(null)
    setFormFor(null)
  }

  const formValid =
    form.selectedDays.length > 0 &&
    Boolean(form.startTime) &&
    Boolean(form.endTime) &&
    (form.lectureDuration ?? 0) >= 1
  const dirty = !sameJson(rows, initial)
  // Days whose break would be orphaned by this draft: a break lives inside the day's school
  // hours, so a day with no timings can keep none. Save drops them (see the timings case in
  // Step2SectionEditor); this tells the user before they commit.
  const droppedBreakDays = breakDays.filter((day) => !usedDays.has(day))

  return (
    <div {...editorRootProps}>
      <p style={helperStyle(t)}>
        {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_QUESTION'})}
      </p>

      {rows.length === 0 && formFor === null && (
        <p style={helperStyle(t)}>
          {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_EMPTY'})}
        </p>
      )}

      {rows.length > 0 && (
        // Header and rows share one grid template (.step2-edit-sheet-list) so the
        // column headings stay over their data at every width.
        <div
          className='step2-edit-sheet-list step2-edit-sheet-list--timings'
          style={{border: `1px solid ${t.border}`, borderRadius: 12, overflow: 'hidden'}}
        >
          <div className='step2-edit-sheet-list-head' style={{background: t.panelBg}}>
            <div style={listHeaderCellStyle(t)}>
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_DAY'})}
            </div>
            <div style={listHeaderCellStyle(t)}>
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_INSTITUTE'})}
            </div>
            <div style={listHeaderCellStyle(t)}>
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_LECTURE_DURATION'})}
            </div>
            <div style={listHeaderCellStyle(t)}>
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_ACTIONS'})}
            </div>
          </div>
          {rows.map((r, idx) => (
            <div
              key={`${r.day}-${idx}`}
              className='step2-edit-sheet-list-row'
              style={{borderTop: `1px solid ${t.border}`}}
            >
              <div>
                <span style={dayBadgeStyle(t)}>
                  {intl.formatMessage({id: `DAY.${normalizeDay(r.day)}` as any})}
                </span>
              </div>
              <div style={{color: t.text, fontSize: '0.9rem', fontWeight: 600, minWidth: 0}}>
                {formatTime24to12(r.startTime)}
                <span style={{color: t.muted, fontWeight: 400}}> — </span>
                {formatTime24to12(r.endTime)}
              </div>
              <div>
                <span style={pillStyle(t)}>
                  {r.lectureDuration ?? 0}{' '}
                  {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_MINS'})}
                </span>
              </div>
              <div className='d-flex gap-1'>
                <button
                  type='button'
                  className='step2-edit-sheet-icon-btn is-edit'
                  aria-label='Edit'
                  onClick={() => openEdit(idx)}
                >
                  <Pencil size={15} />
                </button>
                <button
                  type='button'
                  className='step2-edit-sheet-icon-btn is-delete'
                  aria-label='Delete'
                  onClick={() => (skipDeleteConfirm ? deleteRow(idx) : setConfirmDeleteIdx(idx))}
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {formFor === null && (
        <div className='d-flex gap-2 flex-wrap'>
          {usedDays.size < WEEKDAYS.length && (
            <button
              type='button'
              className='btn btn-light-primary btn-sm d-inline-flex align-items-center gap-2'
              onClick={openAdd}
            >
              <Plus size={15} />
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_ADD_BTN'})}
            </button>
          )}
          {rows.length > 1 && (
            <button
              type='button'
              className='btn btn-light-primary btn-sm d-inline-flex align-items-center gap-2'
              onClick={openBulkEdit}
            >
              <Pencil size={15} />
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_EDIT_ALL_BTN'})}
            </button>
          )}
        </div>
      )}

      {formFor !== null && (
        <div
          className='d-flex flex-column gap-3'
          style={{
            border: `1px solid ${t.border}`,
            borderRadius: 14,
            padding: '1.1rem 1.2rem',
            background: t.panelBg,
          }}
        >
          <div className='d-flex align-items-center gap-2'>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 30,
                height: 30,
                borderRadius: 8,
                background: 'rgba(5,110,233,0.1)',
                color: t.isDark ? '#6ea8f7' : t.accent,
                flexShrink: 0,
              }}
            >
              <Clock size={15} />
            </span>
            <span style={{color: t.text, fontWeight: 700, fontSize: '0.95rem'}}>
              {intl.formatMessage({
                id:
                  formFor === BULK_EDIT
                    ? 'ONBOARDING_WIZARD.STEP2.TIMINGS_EDIT_ALL_BTN'
                    : 'ONBOARDING_WIZARD.STEP2.TIMINGS_MODAL_TITLE',
              })}
            </span>
          </div>
          {formFor === BULK_EDIT && (
            <p style={helperStyle(t)}>
              {intl.formatMessage({
                id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_BULK_OVERWRITE_WARNING',
              })}
            </p>
          )}
          <div>
            <label style={labelStyle(t)}>
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_MODAL_DAYS_LABEL'})}
            </label>
            <p style={{...helperStyle(t), marginBottom: '0.6rem'}}>
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_MODAL_DAYS_HELPER'})}
            </p>
            <div className='d-flex gap-2 flex-wrap'>
              {WEEKDAYS.map((d, i) => (
                <DayChip
                  key={d}
                  label={DAY_LETTERS[i]}
                  selected={form.selectedDays.includes(d)}
                  disabled={isDayDisabled(d)}
                  title={
                    isDayDisabled(d) && formFor === -1
                      ? intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_DUPLICATE_ERROR'})
                      : undefined
                  }
                  onToggle={() => {
                    setFormError(null)
                    setForm((s) => ({
                      ...s,
                      selectedDays: s.selectedDays.includes(d)
                        ? s.selectedDays.filter((x) => x !== d)
                        : [...s.selectedDays, d],
                    }))
                  }}
                  tokens={t}
                />
              ))}
            </div>
            {form.selectedDays.length === 0 && (
              <p className='mt-2' style={errorStyle(t)}>
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_MODAL_NO_DAY_ERROR'})}
              </p>
            )}
          </div>
          {/* Start/End share two equal grid columns (see .step2-edit-sheet-time-row).
              WizardTimePicker replaces the native time input: its OS-drawn picker
              (a full-width wheel sheet on iOS) could not be sized to the phone. */}
          <div className='step2-edit-sheet-time-row'>
            <div className='step2-edit-sheet-time-cell'>
              <label style={labelStyle(t)}>
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_START'})}
              </label>
              <WizardTimePicker
                className='step2-edit-sheet-time-input'
                format='24h'
                icon={false}
                value={form.startTime}
                onChange={(v) => {
                  setForm((s) => ({...s, startTime: v}))
                  setFormError(null)
                }}
                style={inputStyle(t)}
                ariaLabel={intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_START'})}
              />
            </div>
            <div className='step2-edit-sheet-time-cell'>
              <label style={labelStyle(t)}>
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_END'})}
              </label>
              <WizardTimePicker
                className='step2-edit-sheet-time-input'
                format='24h'
                icon={false}
                value={form.endTime}
                onChange={(v) => {
                  setForm((s) => ({...s, endTime: v}))
                  setFormError(null)
                }}
                style={inputStyle(t)}
                ariaLabel={intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_END'})}
              />
            </div>
            <div className='step2-edit-sheet-time-extra'>
              <label style={labelStyle(t)}>
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_LECTURE_DURATION'})}
              </label>
              <div className='d-flex align-items-center gap-2'>
                <input
                  type='number'
                  min={1}
                  max={240}
                  value={form.lectureDuration || ''}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10)
                    setForm((s) => ({
                      ...s,
                      lectureDuration: isNaN(v) ? 0 : Math.max(0, Math.min(240, v)),
                    }))
                    setFormError(null)
                  }}
                  style={{...inputStyle(t), width: 80}}
                />
                <span style={{color: t.muted, fontSize: '0.85rem'}}>
                  {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_MINS'})}
                </span>
              </div>
            </div>
          </div>
          {formError && <p style={errorStyle(t)}>{formError}</p>}
          <div className='d-flex justify-content-end gap-2'>
            <button
              type='button'
              className='step2-edit-sheet-cancel step2-edit-sheet-btn-sm'
              onClick={() => {
                setFormFor(null)
                setFormError(null)
              }}
            >
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_MODAL_CANCEL'})}
            </button>
            <button
              type='button'
              className='step2-edit-sheet-save step2-edit-sheet-btn-sm'
              onClick={applyForm}
              disabled={!formValid}
            >
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_MODAL_SAVE'})}
            </button>
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <p style={helperStyle(t)}>
          {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_APPLIED_INFO'})}
        </p>
      )}

      {droppedBreakDays.length > 0 && (
        <p role='alert' style={errorStyle(t)}>
          {intl.formatMessage(
            {id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_REMOVE_DROPS_BREAKS'},
            {
              count: droppedBreakDays.length,
              days: droppedBreakDays
                .map((day) => intl.formatMessage({id: `DAY.${day}` as any}))
                .join(', '),
            }
          )}
        </p>
      )}

      <ConfirmDeleteDialog
        open={confirmDeleteIdx !== null}
        onCancel={() => setConfirmDeleteIdx(null)}
        onConfirm={(dontShowAgain) => {
          const idx = confirmDeleteIdx
          setConfirmDeleteIdx(null)
          if (dontShowAgain) {
            writeSkipDeleteConfirm('timings')
            setSkipDeleteConfirm(true)
          }
          if (idx === null) return
          deleteRow(idx)
        }}
        tokens={t}
        intl={intl}
      />

      <SheetFooter
        onCancel={onCancel}
        onSave={() => rows.length > 0 && onSave(rows)}
        saveDisabled={!dirty || rows.length === 0}
        isSubmitting={isSubmitting}
        tokens={t}
        intl={intl}
        // While the inline form is open its own Save is the action; two Save
        // buttons stacked on screen read as a mistake.
        hideSave={formFor !== null}
      />
    </div>
  )
}

/* -------------------------------- Breaks ------------------------------------ */

const BreaksEditor: FC<
  EditorCommon & {
    initial: BreakRow[]
    timings: TimingRow[]
    onSave: (breaks: BreakRow[]) => void
  }
> = ({intl, tokens: t, onCancel, isSubmitting, initial, timings, onSave}) => {
  const [rows, setRows] = useState<BreakRow[]>(initial)
  // null = form closed; '' = adding; BULK_EDIT = editing every day's breaks at
  // once; '<DAY>' = editing that day's breaks
  const BULK_EDIT = '*'
  const [formFor, setFormFor] = useState<string | null>(initial.length === 0 ? '' : null)
  // Day awaiting delete confirmation, or null.
  const [confirmDeleteDay, setConfirmDeleteDay] = useState<string | null>(null)
  // "Do not show again" ticked earlier (persisted): delete days without asking.
  const [skipDeleteConfirm, setSkipDeleteConfirm] = useState(() => readSkipDeleteConfirm('breaks'))

  const deleteDay = (day: string) => {
    setRows((prev) => prev.filter((b) => normalizeDay(b.day) !== day))
    if (formFor === day) setFormFor(null)
  }

  /** Days that can hold a break come pre-selected in the add form: every day
   *  with school timings, minus days that already have a break in the draft. */
  const defaultBreakDays = (currentRows: BreakRow[]) => {
    const withTimings = new Set(timings.map((x) => normalizeDay(x.day)))
    const withBreaks = new Set(currentRows.map((b) => normalizeDay(b.day)))
    return WEEKDAYS.filter((d) => withTimings.has(d) && !withBreaks.has(d))
  }

  // One slot per break; edit mode loads every break of the day so a day with
  // two breaks can have both changed (it used to open only the first).
  const [form, setForm] = useState<{selectedDays: string[]; slots: BreakSlot[]}>(() => {
    const prefill = getMidpointBreak(timings)
    return {
      selectedDays: defaultBreakDays(initial),
      slots: [{startTime: prefill.startTime, endTime: prefill.endTime}],
    }
  })
  const [formError, setFormError] = useState<string | null>(null)

  const updateSlot = (idx: number, patch: Partial<BreakSlot>) => {
    setForm((s) => ({...s, slots: s.slots.map((x, i) => (i === idx ? {...x, ...patch} : x))}))
    setFormError(null)
  }
  /** New slot starts where the last one ends and keeps its length. */
  const addSlot = () => {
    setForm((s) => {
      const last = s.slots[s.slots.length - 1]
      if (!last?.startTime || !last?.endTime) {
        return {...s, slots: [...s.slots, {startTime: '', endTime: ''}]}
      }
      const length = Math.max(timeToMinutes(last.endTime) - timeToMinutes(last.startTime), 15)
      const start = timeToMinutes(last.endTime)
      const end = Math.min(start + length, 23 * 60 + 59)
      return {
        ...s,
        slots: [...s.slots, {startTime: minutesToTime(start), endTime: minutesToTime(end)}],
      }
    })
    setFormError(null)
  }
  const removeSlot = (idx: number) => {
    setForm((s) => ({...s, slots: s.slots.filter((_, i) => i !== idx)}))
    setFormError(null)
  }

  const timingDays = useMemo(() => new Set(timings.map((x) => normalizeDay(x.day))), [timings])

  // Weekday order, breaks within a day by start time — so editing Tuesday does
  // not drop it to the bottom of the list (the edited day's rows are re-added).
  const grouped = useMemo(() => {
    const byDay: Record<string, BreakRow[]> = {}
    sortBreaks(rows).forEach((b) => {
      const day = normalizeDay(b.day) || 'MONDAY'
      if (!byDay[day]) byDay[day] = []
      byDay[day].push(b)
    })
    return Object.entries(byDay)
  }, [rows])

  const openAdd = () => {
    const prefill = getMidpointBreak(timings)
    setForm({
      selectedDays: defaultBreakDays(rows),
      slots: [{startTime: prefill.startTime, endTime: prefill.endTime}],
    })
    setFormError(null)
    setFormFor('')
  }

  const openEdit = (day: string) => {
    const dayBreaks = rows
      .filter((b) => normalizeDay(b.day) === day)
      .sort((a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime))
    if (dayBreaks.length === 0) return
    setForm({
      selectedDays: [day],
      slots: dayBreaks.map((b) => ({startTime: b.startTime, endTime: b.endTime})),
    })
    setFormError(null)
    setFormFor(day)
  }

  /**
   * Bulk edit: one form pre-selecting every day that has a break, whose Save
   * REPLACES the whole list — the same break slot(s) applied to all selected
   * days (chips stay toggleable, so days can be added or dropped in the same
   * pass). Seeded from the first day's breaks.
   */
  const openBulkEdit = () => {
    const days = WEEKDAYS.filter((d) => rows.some((b) => normalizeDay(b.day) === d))
    const firstDay = days[0]
    const seed = rows
      .filter((b) => normalizeDay(b.day) === firstDay)
      .sort((a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime))
    const prefill = getMidpointBreak(timings)
    setForm({
      selectedDays: days,
      slots:
        seed.length > 0
          ? seed.map((b) => ({startTime: b.startTime, endTime: b.endTime}))
          : [{startTime: prefill.startTime, endTime: prefill.endTime}],
    })
    setFormError(null)
    setFormFor(BULK_EDIT)
  }

  const applyForm = () => {
    const {selectedDays, slots} = form
    if (selectedDays.length === 0 || slots.some((s) => !s.startTime || !s.endTime)) return
    // Off-days cannot have breaks.
    if (selectedDays.some((d) => !timingDays.has(normalizeDay(d)))) {
      setFormError(intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_BREAK_DAY_DISABLED'}))
      return
    }
    const ranges = slots
      .map((s) => ({start: timeToMinutes(s.startTime), end: timeToMinutes(s.endTime)}))
      .sort((a, b) => a.start - b.start)
    if (ranges.some((r) => r.end <= r.start)) {
      setFormError(
        intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_BREAK_END_AFTER_START'})
      )
      return
    }
    // Every break must fall completely inside that day's institute timings.
    const outsideDay = selectedDays.find((d) => {
      const timing = timings.find((x) => normalizeDay(x.day) === normalizeDay(d))
      if (!timing?.startTime || !timing?.endTime) return false
      const dayStart = timeToMinutes(timing.startTime)
      const dayEnd = timeToMinutes(timing.endTime)
      return ranges.some((r) => r.start < dayStart || r.end > dayEnd)
    })
    if (outsideDay) {
      const timing = timings.find((x) => normalizeDay(x.day) === normalizeDay(outsideDay))
      setFormError(
        intl.formatMessage(
          {id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_BREAK_OUTSIDE_HOURS'},
          {
            day: intl.formatMessage({id: `DAY.${normalizeDay(outsideDay)}` as any}),
            start: formatTime24to12(timing?.startTime ?? ''),
            end: formatTime24to12(timing?.endTime ?? ''),
          }
        )
      )
      return
    }
    // Reject overlaps: between the form's own breaks, and with breaks that stay
    // (the edited day's breaks are replaced wholesale; bulk replaces every day).
    const selfClash = ranges.some((r, i) => i > 0 && r.start < ranges[i - 1].end)
    const kept =
      formFor === BULK_EDIT
        ? []
        : rows.filter((b) => (formFor ? normalizeDay(b.day) !== formFor : true))
    const clash =
      selfClash ||
      selectedDays.some((d) =>
        kept.some(
          (b) =>
            normalizeDay(b.day) === normalizeDay(d) &&
            ranges.some(
              (r) => r.start < timeToMinutes(b.endTime) && r.end > timeToMinutes(b.startTime)
            )
        )
      )
    if (clash) {
      setFormError(intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_BREAK_OVERLAP_ERROR'}))
      return
    }
    const entries = selectedDays.flatMap((day) =>
      slots.map((s) => ({day, startTime: s.startTime, endTime: s.endTime}))
    )
    setRows(sortBreaks(formFor ? [...kept, ...entries] : [...rows, ...entries]))
    setFormError(null)
    setFormFor(null)
  }

  const formValid =
    form.selectedDays.length > 0 && form.slots.every((s) => Boolean(s.startTime && s.endTime))
  // Order-insensitive: the list is re-sorted after every inline save.
  const dirty = !sameJson(sortBreaks(rows), sortBreaks(initial))

  return (
    <div {...editorRootProps}>
      <p style={helperStyle(t)}>
        {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_BREAK_SECTION_TITLE'})}
      </p>

      {rows.length === 0 && formFor === null && (
        <p style={helperStyle(t)}>
          {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_BREAK_EMPTY'})}
        </p>
      )}

      {grouped.length > 0 && (
        // Same shared grid as the timings list (three columns here).
        <div
          className='step2-edit-sheet-list step2-edit-sheet-list--breaks'
          style={{border: `1px solid ${t.border}`, borderRadius: 12, overflow: 'hidden'}}
        >
          <div className='step2-edit-sheet-list-head' style={{background: t.panelBg}}>
            <div style={listHeaderCellStyle(t)}>
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_DAY'})}
            </div>
            <div style={listHeaderCellStyle(t)}>
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_BREAK_COLUMN'})}
            </div>
            <div style={listHeaderCellStyle(t)}>
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_ACTIONS'})}
            </div>
          </div>
          {grouped.map(([day, breaks]) => (
            <div
              key={day}
              className='step2-edit-sheet-list-row'
              style={{borderTop: `1px solid ${t.border}`}}
            >
              <div>
                <span style={dayBadgeStyle(t)}>
                  {intl.formatMessage({id: `DAY.${day}` as any})}
                </span>
              </div>
              <div style={{color: t.text, fontSize: '0.9rem', fontWeight: 600, minWidth: 0}}>
                {breaks
                  .map((b) => `${formatTime24to12(b.startTime)} — ${formatTime24to12(b.endTime)}`)
                  .join(', ')}
              </div>
              <div className='d-flex gap-1'>
                <button
                  type='button'
                  className='step2-edit-sheet-icon-btn is-edit'
                  aria-label='Edit'
                  onClick={() => openEdit(day)}
                >
                  <Pencil size={15} />
                </button>
                <button
                  type='button'
                  className='step2-edit-sheet-icon-btn is-delete'
                  aria-label='Delete'
                  onClick={() => (skipDeleteConfirm ? deleteDay(day) : setConfirmDeleteDay(day))}
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {formFor === null && (
        <div className='d-flex gap-2 flex-wrap'>
          <button
            type='button'
            className='btn btn-light-primary btn-sm d-inline-flex align-items-center gap-2'
            onClick={openAdd}
          >
            <Plus size={15} />
            {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_BREAK_ADD_BTN'})}
          </button>
          {grouped.length > 1 && (
            <button
              type='button'
              className='btn btn-light-primary btn-sm d-inline-flex align-items-center gap-2'
              onClick={openBulkEdit}
            >
              <Pencil size={15} />
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_EDIT_ALL_BTN'})}
            </button>
          )}
        </div>
      )}

      {formFor !== null && (
        <div
          className='d-flex flex-column gap-3'
          style={{
            border: `1px solid ${t.border}`,
            borderRadius: 14,
            padding: '1.1rem 1.2rem',
            background: t.panelBg,
          }}
        >
          <div className='d-flex align-items-center gap-2'>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 30,
                height: 30,
                borderRadius: 8,
                background: 'rgba(5,110,233,0.1)',
                color: t.isDark ? '#6ea8f7' : t.accent,
                flexShrink: 0,
              }}
            >
              <Coffee size={15} />
            </span>
            <span style={{color: t.text, fontWeight: 700, fontSize: '0.95rem'}}>
              {intl.formatMessage({
                id:
                  formFor === BULK_EDIT
                    ? 'ONBOARDING_WIZARD.STEP2.TIMINGS_EDIT_ALL_BTN'
                    : 'ONBOARDING_WIZARD.STEP2.TIMINGS_BREAK_MODAL_TITLE',
              })}
            </span>
          </div>
          {formFor === BULK_EDIT && (
            <p style={helperStyle(t)}>
              {intl.formatMessage({
                id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_BREAK_BULK_OVERWRITE_WARNING',
              })}
            </p>
          )}
          <div>
            <label style={labelStyle(t)}>
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_MODAL_DAYS_LABEL'})}
            </label>
            <div className='d-flex gap-2 flex-wrap'>
              {WEEKDAYS.map((d, i) => {
                // Only days that have institute timings can hold a break; edit
                // mode locks the chips to the day being edited; bulk mode frees
                // every day that has timings.
                const disabled =
                  formFor && formFor !== BULK_EDIT
                    ? normalizeDay(d) !== formFor
                    : !timingDays.has(normalizeDay(d))
                return (
                  <DayChip
                    key={d}
                    label={DAY_LETTERS[i]}
                    selected={form.selectedDays.includes(d)}
                    disabled={disabled}
                    title={
                      disabled && (!formFor || formFor === BULK_EDIT)
                        ? intl.formatMessage({
                            id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_BREAK_DAY_DISABLED',
                          })
                        : undefined
                    }
                    onToggle={() => {
                      setFormError(null)
                      setForm((s) => ({
                        ...s,
                        selectedDays: s.selectedDays.includes(d)
                          ? s.selectedDays.filter((x) => x !== d)
                          : [...s.selectedDays, d],
                      }))
                    }}
                    tokens={t}
                  />
                )
              })}
            </div>
          </div>
          {form.slots.map((slot, idx) => (
            // Same equal-column grid as the timings form; the remove button takes a
            // fixed third column so it never steals width from either time input.
            <div key={idx} className='step2-edit-sheet-time-row'>
              <div className='step2-edit-sheet-time-cell'>
                <label style={labelStyle(t)}>
                  {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_START'})}
                </label>
                <WizardTimePicker
                  className='step2-edit-sheet-time-input'
                  format='24h'
                  icon={false}
                  value={slot.startTime}
                  onChange={(v) => updateSlot(idx, {startTime: v})}
                  style={inputStyle(t)}
                  ariaLabel={intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_START'})}
                />
              </div>
              <div className='step2-edit-sheet-time-cell'>
                <label style={labelStyle(t)}>
                  {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_END'})}
                </label>
                <WizardTimePicker
                  className='step2-edit-sheet-time-input'
                  format='24h'
                  icon={false}
                  value={slot.endTime}
                  onChange={(v) => updateSlot(idx, {endTime: v})}
                  style={inputStyle(t)}
                  ariaLabel={intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_END'})}
                />
              </div>
              {form.slots.length > 1 && (
                <button
                  type='button'
                  className='step2-edit-sheet-icon-btn is-delete step2-edit-sheet-time-remove'
                  aria-label='Remove'
                  onClick={() => removeSlot(idx)}
                >
                  <X size={15} />
                </button>
              )}
            </div>
          ))}
          <button
            type='button'
            className='btn btn-light-primary btn-sm d-inline-flex align-items-center gap-2 align-self-start'
            onClick={addSlot}
          >
            <Plus size={14} />
            {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_BREAK_ADD_ANOTHER_SLOT'})}
          </button>
          {formError && <p style={errorStyle(t)}>{formError}</p>}
          <div className='d-flex justify-content-end gap-2'>
            <button
              type='button'
              className='step2-edit-sheet-cancel step2-edit-sheet-btn-sm'
              onClick={() => {
                setFormFor(null)
                setFormError(null)
              }}
            >
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_MODAL_CANCEL'})}
            </button>
            <button
              type='button'
              className='step2-edit-sheet-save step2-edit-sheet-btn-sm'
              onClick={applyForm}
              disabled={!formValid}
            >
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_MODAL_SAVE'})}
            </button>
          </div>
        </div>
      )}

      <ConfirmDeleteDialog
        open={confirmDeleteDay !== null}
        onCancel={() => setConfirmDeleteDay(null)}
        onConfirm={(dontShowAgain) => {
          const day = confirmDeleteDay
          setConfirmDeleteDay(null)
          if (dontShowAgain) {
            writeSkipDeleteConfirm('breaks')
            setSkipDeleteConfirm(true)
          }
          if (day === null) return
          deleteDay(day)
        }}
        tokens={t}
        intl={intl}
      />

      <SheetFooter
        onCancel={onCancel}
        onSave={() => onSave(rows)}
        saveDisabled={!dirty}
        isSubmitting={isSubmitting}
        tokens={t}
        intl={intl}
        // While the inline form is open its own Save is the action, and with
        // nothing listed (and nothing to clear) there is nothing to commit —
        // either way the footer keeps just Cancel.
        hideSave={formFor !== null || (rows.length === 0 && initial.length === 0)}
      />
    </div>
  )
}

export default Step2SectionEditor
